import { Component } from 'react'
import _ from 'lodash'
import { Route, Switch, Redirect } from 'react-router-dom'
import classNames from 'classnames'
import {
  getRBAPermission,
  isPermissionDisabled,
  recordLastAvailResult,
  isInManageRoute,
  showLoadingMask,
} from './ui-lib/utils'
import { logout } from '../../framework/actions'
import { PathNotAvailable, NoPathAvailable, FawkesNotAvailable, NoTenantAccess } from '../common/PathNotAvailable'
import { globalAvail, tsgAccessAvail } from '../common/utils'
import LoadingPlaceholder from '../common/LoadingPlaceholder'
import { hasMicroApp } from '../../framework/microApps'
import { hasTsgSupport } from '../../framework/tsg'
import history from '../../framework/history'
import { getFrameworkVar } from '../../framework/states'
import {
  MGMT_OVERVIEW_ROUTE,
  PA_DEFAULT_ROUTE,
  FAWKES_MICRO_APP_NAME,
  MSP_PATH_PREFIX,
  FAWKES_APP_ID,
} from '../../framework/constants'
import { PANWDS_TW2_CLS } from '../../patches/panwds-constants'
import StartupManager from './StartupManager'
import './RouteViewer.scss'
import type { VFC, ReactElement } from 'react'
import type { ComponentOrLazyFactory, ComponentWrapperProps, NavItem } from '../../framework/microAppTypes'
import type { EventEmitter } from '../../framework/events'
import type { AnyState, FeatureInfo, StoreState, StateRef, Dispatch, ConfigLocation } from '../../framework/stateTypes'

interface RouteViewerProps {
  appBar: ReactElement
  sideMenuDrawer: ReactElement
  loggedInUser: AnyState
  tenant: AnyState
  navigation: NavItem[]
  configLocation: ConfigLocation
  licenseInfo: AnyState
  featuresInfo: FeatureInfo[]
  hideSideMenu: () => void
  _showSideMenu: boolean
  state: StoreState
  getState: () => StoreState
  stateRef: StateRef
  dispatch: Dispatch
  events: EventEmitter
  routeContext: AnyState
  RouteWrapper: VFC<ComponentWrapperProps>
  salt: string
  tokenInfo: any
  defaultRoute: string | undefined
}

type NavRoutes = ReactElement[] // & { defaultNav?: NavItem }

const NO_REDIRECT = 'no-redirect'

export default class RouteViewer extends Component<RouteViewerProps, {}> {
  isExcludedPath(path: string) {
    const excludedPathReg = /manage\/mobile-users-ep.+?\/identityRedistribution/g
    return excludedPathReg.test(path)
  }

  getRBAFromRoute(routeUrl: string | undefined) {
    let rba = routeUrl ? routeUrl.replace(/\//g, '.') : ''
    rba = rba && rba.startsWith('.') ? rba.slice(1) : rba
    return rba
  }

  isValidRoute = (nav: NavItem): boolean | 'loading' | 'no-redirect' => {
    if (nav.isLoading && nav.isLoading?.()) {
      return 'loading' // is loading
    }
    if (nav.availFallbackRedirect === NO_REDIRECT) {
      return NO_REDIRECT
    }
    const {
      configLocation,
      licenseInfo,
      featuresInfo,
      state: {
        auth,
        main,
      }
    } = this.props
    const {
      instances,
      access,
      tsg_id,
    } = auth || {}
    const obj = {
      ...configLocation,
      configLocation,
      location: history.location,
      licenseInfo,
      featuresInfo,
      instances,
      access,
      tsg_id,
      auth,
      main,
      navItem: nav,
    }
    try {
      const noRBACheck = nav.noRBACheck || hasTsgSupport()
      if (!noRBACheck &&
        isPermissionDisabled(getRBAPermission(this.getRBAFromRoute(nav.rbaPath || nav.path)))) {
        return false // TODO: should be removed once non-tsg is gone
      }
      const isAvail = !nav.avail || Boolean(nav.avail(obj, licenseInfo, featuresInfo))
      recordLastAvailResult(nav, isAvail, 'route')
      return isAvail && Boolean(nav.bypassGlobalAvail || globalAvail()) &&
        Boolean(nav.bypassTsgAvail || tsgAccessAvail())
    }
    catch (e) {
      console.error('failed to validate route due to exception', nav, e)
      return false
    }
  }

  getFirstAvailDefaultRouteURL(navItems: NavItem[]) {
    let firstAvailDefaultRouteURL = '/'
    navItems.forEach((nav) => {
      if (firstAvailDefaultRouteURL === '/') {
        const isValid = this.isValidRoute(nav)
        if (nav.path && isValid && isValid !== NO_REDIRECT) {
          firstAvailDefaultRouteURL = nav.path
          return false
        }
        else if (nav.children) {
          firstAvailDefaultRouteURL = this.getFirstAvailDefaultRouteURL(nav.children)
        }
      }
      else {
        return false
      }
    })
    return firstAvailDefaultRouteURL
  }

  logoutRoute = () => {
    logout()
    return null
  }

  redirectFawkes = () => {
    if (!globalAvail()) {
      return <NoPathAvailable />
    }

    const errorState = this.props.state.main?.fawkesSessionError || {}
    if (errorState.message) {
      return <FawkesNotAvailable {...errorState} />
    }
    const fawkesRedirect = StartupManager.getFawkesDefaultRedirect() || MGMT_OVERVIEW_ROUTE
    let microAppNameToCheck: string | undefined
    let firstLevelPath: string | undefined
    if (isInManageRoute(fawkesRedirect)) {
      microAppNameToCheck = FAWKES_MICRO_APP_NAME
      firstLevelPath = MGMT_OVERVIEW_ROUTE
    }
    else if (fawkesRedirect.startsWith('/insights')) {
      microAppNameToCheck = 'pai'
      firstLevelPath = '/insights'
    }
    else if (fawkesRedirect.startsWith('/dem')) {
      microAppNameToCheck = 'dem'
      firstLevelPath = '/dem'
    }
    if (microAppNameToCheck && !hasMicroApp(microAppNameToCheck)) {
      console.error(`micro-app "${microAppNameToCheck}" is not available for ${PA_DEFAULT_ROUTE} redirect to ${fawkesRedirect}`)
      return <FawkesNotAvailable message={`${firstLevelPath || PA_DEFAULT_ROUTE} is not available.`} from={errorState.from} />
    }
    const matchedItem = firstLevelPath && hasTsgSupport() && this.props.navigation.find(item => item.path === firstLevelPath)
    const isMatchedItemValid = matchedItem && this.isValidRoute(matchedItem)
    if (firstLevelPath === MGMT_OVERVIEW_ROUTE && !isMatchedItemValid && getFrameworkVar('sase')) {
      const url = this.props.state?.auth?.instances?.get(FAWKES_APP_ID)?.url
      if (url && new URL(url).origin !== window.location.origin && hasMicroApp(FAWKES_MICRO_APP_NAME)) {
        console.warn('redirect to url due to current prisma-access not accessible', url)
        showLoadingMask()
        window.location.href = url
        return null
      }
      else if (url) {
        return <FawkesNotAvailable message={`${firstLevelPath} is not available.`} from={errorState.from} />
      }
    }
    if (matchedItem && !isMatchedItemValid) {
      if (this.getDefaultRoute()?.startsWith(MSP_PATH_PREFIX)) {
        return <FawkesNotAvailable message={`${firstLevelPath} is not available.`} from={errorState.from} />
      }
    }
    return <Redirect to={fawkesRedirect} />
  }

  renderRouteComponent = (routes: NavRoutes, navItem: NavItem, parent?: NavItem, contentClassName?: string) => {
    if (!navItem.path) {
      return
    }
    const RouteWrapper = this.props.RouteWrapper
    if (navItem.redirectTo) {
      if (navItem.component && navItem.exact) {
        console.warn('redirectTo is not allowed if the nav item has component with exact: true')
      }
      else {
        routes.push(<Route
          key={`${navItem.path}-redirect`}
          path={navItem.path}
          exact={true}
          render={() => {
            const to = _.isFunction(navItem.redirectTo) ? navItem.redirectTo() : navItem.redirectTo!
            return <Redirect to={to} />
          }}
        />)
      }
    }
    // all the leaf nodes with component will create a route
    routes.push(
      <Route
        key={navItem.path}
        exact={navItem.exact}
        path={navItem.path}
        render={(props) => {
          const isAvail = this.isValidRoute(navItem)
          if (isAvail === 'loading') {
            return <LoadingPlaceholder />
          }
          if (isAvail && !this.isExcludedPath(props.location.pathname)) {
            const cls = classNames(
              'app-body-container container-fluid',
              !(contentClassName && /\bpanwds-(?:tw3|mixed)\b/.test(contentClassName)) && PANWDS_TW2_CLS, // tw2 by default now
              contentClassName,
            )
            return (
              <div className={cls}>
                <RouteWrapper
                  navItem={navItem}
                  hasParent={Boolean(parent)}
                  renderProps={props}
                  parentProps={this.props}
                />
              </div>
            )
          }
          if (navItem.availFallbackRedirect) {
            const redirect = _.isFunction(navItem.availFallbackRedirect) ? navItem.availFallbackRedirect() :
              _.isString(navItem.availFallbackRedirect) ? navItem.availFallbackRedirect : undefined
            if (redirect?.startsWith('/')) {
              return <Redirect to={redirect} />
            }
            else if (redirect) {
              console.error('the result of availFallbackRedirect should be a path starts with /')
            }
          }
          const defaultRoute = this.getDefaultRoute()
          const defaultRouteNotAvail = Boolean(defaultRoute &&
            (navItem.path === defaultRoute || (
              defaultRoute === PA_DEFAULT_ROUTE && isInManageRoute(navItem.path!)
            ))
          )
          if (!globalAvail()) {
            return <NoPathAvailable />
          }
          if (!tsgAccessAvail()) {
            return <NoTenantAccess auth={this.props.state?.auth} isIPRestricted={Boolean(this.props.state?.auth?.instances?.error?.ipRestricted)}/>
          }
          if (!defaultRouteNotAvail) {
            // no permission for this route. redirect to defaultRoute
            return <Redirect to={defaultRoute || '/'} />
          }
          return <PathNotAvailable location={props.location} />
        }}
      />
    )
  }

  renderNavigation = (routes: NavRoutes, nav: NavItem[], parent?: NavItem, contentClassName?: string) => {
    for (const navItem of nav) {
      if (!navItem) {
        continue
      }

      if (navItem.children && !(navItem.component as ComponentOrLazyFactory)?.layout && this.isValidRoute(navItem)) {
        // nested child navigation - we will display as collapsible side section
        this.renderNavigation(routes, navItem.children, navItem.name, navItem.contentClassName || contentClassName)
      }

      if (navItem.component) { // even if navItem.children is present
        this.renderRouteComponent(routes, navItem, parent, navItem.contentClassName || contentClassName)
      }

      // if (this.props.defaultRoute && navItem.path === this.props.defaultRoute) {
      //   routes.defaultNav = navItem
      // }
    }
  }

  getDefaultRoute = () => {
    const { defaultRoute } = this.props
    return defaultRoute && defaultRoute !== '/' ? defaultRoute : undefined
  }

  render() {
    const navItems = [] as NavRoutes
    const isGlobalAvail = globalAvail()
    const hasPARoute = this.props.navigation.some(nav => nav?.path === PA_DEFAULT_ROUTE)
    const navigation = isGlobalAvail ? this.props.navigation :
      this.props.navigation.filter(navItem => navItem.bypassGlobalAvail)
    this.renderNavigation(navItems, _.orderBy(navigation, ['path'], ['desc']))
    const defaultRoute = this.getDefaultRoute()
    return (
      <div className='sparky-app app'>
        <div className={'app-bar-parent'}>
          {this.props.appBar}
        </div>
        <div className={'left-nav-parent'}>
          {this.props.sideMenuDrawer}
        </div>
        <div id='mainframe' className='app-body spf-bg-gray-100 dark:spf-bg-gray-950'>
          <Switch>
            {navItems}
            <Route key='logout' path='/logout' render={this.logoutRoute} />
            <Route key='auth-callback' path='/authentication/callback' render={() => null} />
            {!hasPARoute && <Route key='prisma-access' exact path={PA_DEFAULT_ROUTE} render={this.redirectFawkes} />}
            {isGlobalAvail ?
              (defaultRoute && navItems.length ? <Redirect from='/' to={defaultRoute} /> : null) :
              <Route key='none' path='/' component={NoPathAvailable} />
            }
          </Switch>
        </div>
      </div>
    )
  }
}
